import type { BatchMessageResult, CallEvent, ContactDirectoryDetail, ContactDirectoryResponse, Conversation, DashboardSnapshot, LookupProviderHealth, LookupRequest, LookupSaveResult, LookupSearch, LookupUsage, MessageAttachmentUpload, MessageEvent, MessageTemplate, MessageTemplateApplication, NotificationCategory, NotificationFeed, PushDeliveryDiagnostics, PushPreferences, PushSettings, PushSubscriptionPayload, PushTestResult, SendMessageRequest, StartClickToCallRequest } from '../domain';

export class AuthenticationRequiredError extends Error {}

export class ApiRequestError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
  }
}

export interface CommunicationsRepository {
  getDashboardSnapshot(): Promise<DashboardSnapshot>;
  getConversation(businessId: string, contactPhone: string): Promise<Conversation>;
  getContacts(businessId?: string, query?: string): Promise<ContactDirectoryResponse>;
  getContact(contactId: string, businessId?: string): Promise<ContactDirectoryDetail>;
  updateContact(contactId: string, input: { displayName: string; phoneNumber: string }): Promise<ContactDirectoryDetail>;
  uploadContactPhoto(contactId: string, file: File): Promise<void>;
  removeContactPhoto(contactId: string): Promise<void>;
  uploadMessageAttachment(file: File): Promise<MessageAttachmentUpload>;
  getMessageTemplates(businessId: string): Promise<MessageTemplate[]>;
  createMessageTemplate(input: { businessId: string; name: string; text: string; attachments?: Array<{ objectKey: string; name: string }> }): Promise<MessageTemplate>;
  applyMessageTemplate(templateId: string): Promise<MessageTemplateApplication>;
  deleteMessageTemplate(templateId: string): Promise<void>;
  getMessage(messageId: string): Promise<MessageEvent>;
  deleteMessage(messageId: string): Promise<void>;
  deleteConversation(businessId: string, contactPhone: string): Promise<number>;
  sendMessage(request: SendMessageRequest): Promise<MessageEvent>;
  sendBatchMessage(request: Omit<SendMessageRequest, 'to'> & { recipients: string[] }): Promise<BatchMessageResult>;
  startClickToCall(request: StartClickToCallRequest): Promise<CallEvent>;
  getCall(callId: string): Promise<CallEvent>;
  deleteCall(callId: string): Promise<void>;
  updateBusinessColor(businessId: string, color: string): Promise<void>;
  uploadBusinessLogo(businessId: string, file: File): Promise<void>;
  removeBusinessLogo(businessId: string): Promise<void>;
  markConversationRead(businessId: string, contactPhone: string): Promise<void>;
  markCallReviewed(callId: string): Promise<void>;
  dismissNeedsReview(kind: 'message' | 'call', communicationId: string): Promise<void>;
  getPushSettings(endpoint?: string): Promise<PushSettings>;
  savePushSubscription(subscription: PushSubscriptionPayload, preferences: PushPreferences): Promise<void>;
  deletePushSubscription(endpoint: string): Promise<void>;
  getPushDiagnostics(endpoint: string): Promise<PushDeliveryDiagnostics>;
  sendTestNotification(endpoint: string, businessId: string, category: NotificationCategory): Promise<PushTestResult>;
  getNotifications(): Promise<NotificationFeed>;
  markNotificationsSeen(ids: string[]): Promise<void>;
  markAllNotificationsSeen(): Promise<void>;
  dismissNotification(notificationId: string): Promise<void>;
  createLookup(request: LookupRequest): Promise<LookupSearch>;
  getLookup(id: string): Promise<LookupSearch>;
  getLookupHistory(limit?: number): Promise<LookupSearch[]>;
  getLookupProviders(): Promise<LookupProviderHealth[]>;
  getLookupUsage(): Promise<LookupUsage>;
  saveLookup(id: string, notes?: string, tags?: string[]): Promise<LookupSaveResult>;
  refreshLookup(id: string): Promise<LookupSearch>;
  runPaidLookupEnrichment(id: string): Promise<LookupSearch>;
  login(email: string, password: string): Promise<void>;
}

class ApiCommunicationsRepository implements CommunicationsRepository {
  async getDashboardSnapshot(): Promise<DashboardSnapshot> {
    return api<DashboardSnapshot>('/api/v1/dashboard');
  }

  async getConversation(businessId: string, contactPhone: string): Promise<Conversation> {
    const query = new URLSearchParams({ business_id: businessId, contact_phone: contactPhone });
    return api<Conversation>(`/api/v1/conversations?${query}`);
  }

  async getContacts(businessId = 'all', query = ''): Promise<ContactDirectoryResponse> {
    const params = new URLSearchParams({ business_id: businessId, limit: '500' });
    if (query.trim()) params.set('q', query.trim());
    return api<ContactDirectoryResponse>(`/api/v1/contacts?${params}`);
  }

  async getContact(contactId: string, businessId = 'all'): Promise<ContactDirectoryDetail> {
    const params = new URLSearchParams({ business_id: businessId });
    const response = await api<{ data: ContactDirectoryDetail }>(`/api/v1/contacts/${encodeURIComponent(contactId)}?${params}`);
    return response.data;
  }

  async updateContact(contactId: string, input: { displayName: string; phoneNumber: string }): Promise<ContactDirectoryDetail> {
    const response = await api<{ data: ContactDirectoryDetail }>(`/api/v1/contacts/${encodeURIComponent(contactId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return response.data;
  }

  async uploadContactPhoto(contactId: string, file: File): Promise<void> {
    await api(`/api/v1/contacts/${encodeURIComponent(contactId)}/photo`, {
      method: 'POST',
      headers: { 'Content-Type': file.type },
      body: file,
    });
  }

  async removeContactPhoto(contactId: string): Promise<void> {
    await api(`/api/v1/contacts/${encodeURIComponent(contactId)}/photo`, { method: 'DELETE' });
  }

  async uploadMessageAttachment(file: File): Promise<MessageAttachmentUpload> {
    const response = await api<{ data: MessageAttachmentUpload }>('/api/v1/message-attachments', {
      method: 'POST',
      headers: {
        'Content-Type': file.type,
        'X-File-Name': encodeURIComponent(file.name),
      },
      body: file,
    });
    return response.data;
  }

  async getMessageTemplates(businessId: string): Promise<MessageTemplate[]> {
    const response = await api<{ data: MessageTemplate[] }>(`/api/v1/message-templates?${new URLSearchParams({ business_id: businessId })}`);
    return response.data;
  }

  async createMessageTemplate(input: { businessId: string; name: string; text: string; attachments?: Array<{ objectKey: string; name: string }> }): Promise<MessageTemplate> {
    const response = await api<{ data: MessageTemplate }>('/api/v1/message-templates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return response.data;
  }

  async applyMessageTemplate(templateId: string): Promise<MessageTemplateApplication> {
    const response = await api<{ data: MessageTemplateApplication }>(`/api/v1/message-templates/${encodeURIComponent(templateId)}/apply`, { method: 'POST' });
    return response.data;
  }

  async deleteMessageTemplate(templateId: string): Promise<void> {
    await api(`/api/v1/message-templates/${encodeURIComponent(templateId)}`, { method: 'DELETE' });
  }

  async getMessage(messageId: string): Promise<MessageEvent> {
    const response = await api<{ data: MessageEvent }>(`/api/v1/messages/${encodeURIComponent(messageId)}`);
    return response.data;
  }

  async deleteMessage(messageId: string): Promise<void> {
    await api(`/api/v1/messages/${encodeURIComponent(messageId)}`, { method: 'DELETE' });
  }

  async deleteConversation(businessId: string, contactPhone: string): Promise<number> {
    const query = new URLSearchParams({ business_id: businessId, contact_phone: contactPhone });
    const response = await api<{ data: { hiddenCount: number } }>(`/api/v1/conversations?${query}`, { method: 'DELETE' });
    return response.data.hiddenCount;
  }

  async sendMessage(request: SendMessageRequest): Promise<MessageEvent> {
    const response = await api<{ data: MessageEvent }>('/api/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': request.idempotencyKey },
      body: JSON.stringify({
        businessId: request.businessId,
        contactId: request.contactId,
        to: request.to,
        text: request.text,
        mediaObjectKeys: request.mediaObjectKeys,
      }),
    });
    return response.data;
  }

  async sendBatchMessage(request: Omit<SendMessageRequest, 'to'> & { recipients: string[] }): Promise<BatchMessageResult> {
    const response = await api<{ data: BatchMessageResult }>('/api/v1/messages/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': request.idempotencyKey },
      body: JSON.stringify({
        businessId: request.businessId,
        recipients: request.recipients,
        text: request.text,
        mediaObjectKeys: request.mediaObjectKeys,
      }),
    });
    return response.data;
  }

  async startClickToCall(request: StartClickToCallRequest): Promise<CallEvent> {
    const response = await api<{ data: CallEvent }>('/api/v1/calls/click-to-call', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': request.idempotencyKey },
      body: JSON.stringify(request),
    });
    return response.data;
  }

  async getCall(callId: string): Promise<CallEvent> {
    const response = await api<{ data: CallEvent }>(`/api/v1/calls/${encodeURIComponent(callId)}`);
    return response.data;
  }

  async deleteCall(callId: string): Promise<void> {
    await api(`/api/v1/calls/${encodeURIComponent(callId)}`, { method: 'DELETE' });
  }

  async updateBusinessColor(businessId: string, color: string): Promise<void> {
    await api(`/api/v1/businesses/${encodeURIComponent(businessId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ color }),
    });
  }

  async uploadBusinessLogo(businessId: string, file: File): Promise<void> {
    await api(`/api/v1/businesses/${encodeURIComponent(businessId)}/logo`, {
      method: 'POST',
      headers: { 'Content-Type': file.type },
      body: file,
    });
  }

  async removeBusinessLogo(businessId: string): Promise<void> {
    await api(`/api/v1/businesses/${encodeURIComponent(businessId)}/logo`, { method: 'DELETE' });
  }

  async markConversationRead(businessId: string, contactPhone: string): Promise<void> {
    await api<void>('/api/v1/conversations/read', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ businessId, contactPhone }),
    });
  }

  async markCallReviewed(callId: string): Promise<void> {
    await api<void>(`/api/v1/calls/${encodeURIComponent(callId)}/reviewed`, { method: 'POST' });
  }

  async dismissNeedsReview(kind: 'message' | 'call', communicationId: string): Promise<void> {
    await api<void>(`/api/v1/communications/${kind}/${encodeURIComponent(communicationId)}/reviewed`, { method: 'POST' });
  }

  async getPushSettings(endpoint?: string): Promise<PushSettings> {
    const query = endpoint ? `?${new URLSearchParams({ endpoint })}` : '';
    return api<PushSettings>(`/api/v1/push/settings${query}`);
  }

  async savePushSubscription(subscription: PushSubscriptionPayload, preferences: PushPreferences): Promise<void> {
    await api('/api/v1/push/subscription', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscription, preferences }),
    });
  }

  async deletePushSubscription(endpoint: string): Promise<void> {
    await api('/api/v1/push/subscription', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    });
  }

  async getPushDiagnostics(endpoint: string): Promise<PushDeliveryDiagnostics> {
    return api<PushDeliveryDiagnostics>(`/api/v1/push/diagnostics?${new URLSearchParams({ endpoint })}`);
  }

  async sendTestNotification(endpoint: string, businessId: string, category: NotificationCategory): Promise<PushTestResult> {
    const response = await api<{ data: PushTestResult }>('/api/v1/push/test', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint, businessId, category }),
    });
    return response.data;
  }

  async getNotifications(): Promise<NotificationFeed> {
    return api<NotificationFeed>('/api/v1/notifications?limit=30');
  }

  async markNotificationsSeen(ids: string[]): Promise<void> {
    await api('/api/v1/notifications/seen', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
  }

  async markAllNotificationsSeen(): Promise<void> {
    await api('/api/v1/notifications/seen', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ all: true }),
    });
  }

  async dismissNotification(notificationId: string): Promise<void> {
    await api(`/api/v1/notifications/${encodeURIComponent(notificationId)}`, { method: 'DELETE' });
  }

  async createLookup(request: LookupRequest): Promise<LookupSearch> {
    const response = await api<{ data: LookupSearch }>('/api/v1/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return response.data;
  }

  async getLookup(id: string): Promise<LookupSearch> {
    const response = await api<{ data: LookupSearch }>(`/api/v1/lookup/${encodeURIComponent(id)}`);
    return response.data;
  }

  async getLookupHistory(limit = 30): Promise<LookupSearch[]> {
    const response = await api<{ data: LookupSearch[] }>(`/api/v1/lookup/history?limit=${Math.max(1, Math.min(100, limit))}`);
    return response.data;
  }

  async getLookupProviders(): Promise<LookupProviderHealth[]> {
    const response = await api<{ data: LookupProviderHealth[] }>('/api/v1/lookup/providers');
    return response.data;
  }

  async getLookupUsage(): Promise<LookupUsage> {
    const response = await api<{ data: LookupUsage }>('/api/v1/lookup/usage');
    return response.data;
  }

  async saveLookup(id: string, notes = '', tags: string[] = []): Promise<LookupSaveResult> {
    const response = await api<{ data: LookupSaveResult }>(`/api/v1/lookup/${encodeURIComponent(id)}/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes, tags }),
    });
    return response.data;
  }

  async refreshLookup(id: string): Promise<LookupSearch> {
    const response = await api<{ data: LookupSearch }>(`/api/v1/lookup/${encodeURIComponent(id)}/refresh`, { method: 'POST' });
    return response.data;
  }

  async runPaidLookupEnrichment(id: string): Promise<LookupSearch> {
    const response = await api<{ data: LookupSearch }>(`/api/v1/lookup/${encodeURIComponent(id)}/paid-enrichment`, { method: 'POST' });
    return response.data;
  }

  async login(email: string, password: string): Promise<void> {
    await api('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  }
}

async function api<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, { ...init, credentials: 'include' });
  if (response.status === 401) throw new AuthenticationRequiredError('Sign in to continue.');
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: { message?: string; code?: string } };
    throw new ApiRequestError(body.error?.message || `Request failed with HTTP ${response.status}.`, response.status, body.error?.code);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const communicationsRepository: CommunicationsRepository = new ApiCommunicationsRepository();
